import React from 'react';
import { format } from 'date-fns';
import {
  Clock,
  Repeat,
  Edit2,
  Trash2,
  Power,
  PowerOff,
  Facebook,
  Instagram,
  Linkedin,
  Building2,
  Twitter,
  Globe,
  AlertTriangle,
  RefreshCw,
  ExternalLink,
  Film,
  Layers,
  ImageIcon,
  ShieldAlert,
  Smartphone,
  CheckCircle2
} from 'lucide-react';
import { FormattedText } from '../../lib/formatContent';
import { getContentTypeBadgeColors, getContentTypeBadgeLabel } from '../task/ReelsStoryConstants';

const renderPlatformIcon = (platform: string) => {
  switch (platform.toLowerCase()) {
    case 'facebook':
      return <Facebook className="w-5 h-5 text-blue-600" />;
    case 'instagram':
      return <Instagram className="w-5 h-5 text-pink-600" />;
    case 'linkedin':
      return <Linkedin className="w-5 h-5 text-blue-700" />;
    case 'google_business':
      return <Building2 className="w-5 h-5 text-red-600" />;
    case 'x':
      return <Twitter className="w-5 h-5 text-black" />;
    default:
      return <Globe className="w-5 h-5 text-gray-600" />;
  }
};

interface TaskDetailsProps {
  task: any;
  onAction: (action: 'edit' | 'delete' | 'toggle') => void;
  onRetry?: () => void;
}

const getErrorInfo = (task: any) => {
  // Only show error for the LATEST post — if a newer non-error post exists,
  // the old error is from a previous execution and should be ignored
  const sortedPosts = (task?.posts || [])
    .slice()
    .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  
  const latestPost = sortedPosts[0];
  if (!latestPost || latestPost.status !== 'error') return null;
  
  const errorPost = latestPost;

  const errorMessage = errorPost.metadata?.error ||
    errorPost.metadata?.error_details?.message ||
    'Publication failed';
  const errorTimestamp = errorPost.metadata?.error_timestamp ||
    errorPost.metadata?.first_error_time ||
    errorPost.updated_at;
  const attemptCount = errorPost.publication_attempt_count ||
    errorPost.metadata?.retry_count ||
    1;
  const maxAttempts = 12;
  const retryInterval = 5;
  const lastAttempt = errorPost.last_publish_attempt || errorPost.updated_at;
  const firstErrorTime = errorPost.metadata?.first_error_time || lastAttempt;

  let errorType: 'identity' | 'account_restricted' | 'permission' | 'token' | 'content' | 'media' | 'unknown' = 'unknown';
  let actionRequired = 'System will automatically retry publication.';
  let actionLink: string | null = null;
  let canRetry = true;
  const isPermanent = errorPost.metadata?.is_permanent === true;
  const errorCode = errorPost.metadata?.error_code || '';
  const resolutionInstructions = errorPost.metadata?.resolution_instructions || '';

  // Identity verification required (detected by backend or by message content)
  if (
    errorCode === 'FACEBOOK_IDENTITY_REQUIRED' ||
    errorPost.metadata?.error_type === 'identity_verification' ||
    errorMessage.toLowerCase().includes('tożsamość') ||
    errorMessage.toLowerCase().includes('identity') ||
    errorMessage.toLowerCase().includes('verify your identity')
  ) {
    errorType = 'identity';
    actionRequired = resolutionInstructions || 'Otwórz aplikację Facebook na telefonie i potwierdź swoją tożsamość dla tej strony.';
    canRetry = false;
  // Account restricted/disabled
  } else if (
    errorCode === 'FACEBOOK_ACCOUNT_RESTRICTED' ||
    errorPost.metadata?.error_type === 'account_restricted' ||
    errorMessage.toLowerCase().includes('restricted') ||
    errorMessage.toLowerCase().includes('disabled') ||
    errorMessage.toLowerCase().includes('zablokowane')
  ) {
    errorType = 'account_restricted';
    actionRequired = resolutionInstructions || 'Twoje konto lub strona Facebook została ograniczona. Sprawdź ustawienia konta na Facebook.';
    canRetry = false;
  } else if (errorMessage.toLowerCase().includes('permission') || errorCode === 'FACEBOOK_PERMISSION_DENIED') {
    errorType = 'permission';
    actionRequired = resolutionInstructions || 'Brak wymaganych uprawnień. Połącz ponownie konto Facebook.';
    actionLink = '/accounts';
    canRetry = false;
  } else if (errorMessage.toLowerCase().includes('token') || errorMessage.toLowerCase().includes('oauth') || errorCode === 'FACEBOOK_TOKEN_EXPIRED') {
    errorType = 'token';
    actionRequired = resolutionInstructions || 'Token dostępu wygasł. Połącz ponownie konto Facebook. 💡 Sprawdź też, czy Facebook nie usunął Cię z zarządzania fanpage – wejdź w fanpage → Ustawienia → Konfiguracja Strony → Dostęp do strony i upewnij się, że widniejesz jako „Osoba z dostępem do Facebooka" z pełnymi uprawnieniami.';
    actionLink = '/accounts';
    canRetry = false;
  } else if (errorMessage.toLowerCase().includes('caption') || errorMessage.toLowerCase().includes('too long')) {
    errorType = 'content';
    actionRequired = 'Edytuj treść posta, aby spełniała wymagania platformy.';
    canRetry = false;
  } else if (errorMessage.toLowerCase().includes('media') || errorMessage.toLowerCase().includes('2207027')) {
    errorType = 'media';
    actionRequired = 'Media jest nadal przetwarzane. System ponowi próbę automatycznie.';
  }

  const timeElapsed = firstErrorTime ?
    Math.floor((Date.now() - new Date(firstErrorTime).getTime()) / 60000) : 0;
  const timeRemaining = Math.max(0, 60 - timeElapsed);
  const attemptsRemaining = Math.max(0, maxAttempts - attemptCount);
  const isRetryActive = canRetry && attemptCount < maxAttempts && timeRemaining > 0;

  let nextRetry: Date | null = null;
  if (isRetryActive && lastAttempt) {
    nextRetry = new Date(new Date(lastAttempt).getTime() + retryInterval * 60000);
  }

  return {
    message: errorMessage,
    timestamp: errorTimestamp,
    type: errorType,
    actionRequired,
    actionLink,
    canRetry,
    attemptCount,
    maxAttempts,
    attemptsRemaining,
    isRetryActive,
    nextRetry,
    timeRemaining,
    retryInterval,
    isPermanent,
    errorCode,
    resolutionInstructions
  };
};

export const TaskDetails: React.FC<TaskDetailsProps> = ({ task, onAction, onRetry }) => {
  // Determine task title based on status and posts
  const getTaskTitle = () => {
    // Check if there are any published posts
    // A post is considered published if:
    // 1. status is 'published', OR
    // 2. it has a platform_post_id or platform_post_url (means it was posted even if status wasn't updated)
    // For recurring tasks, only check posts from today
    const hasPublishedPosts = task.posts?.some((post: any) => {
      const isPublished = post.status === 'published' || post.platform_post_id || post.platform_post_url;

      if (!isPublished) return false;

      // For recurring tasks, check if the post was created on the same day as the task
      if (task.cycle && task.cycle !== 'once') {
        const taskDate = new Date(task.scheduled_for);
        taskDate.setHours(0, 0, 0, 0);
        const postDate = new Date(post.created_at);
        postDate.setHours(0, 0, 0, 0);
        return postDate.getTime() === taskDate.getTime();
      }

      return true;
    });

    if (hasPublishedPosts) {
      // Task has published posts
      if (task.cycle === 'once') {
        return 'Opublikowane zadanie';
      } else {
        return 'Zadanie cykliczne (aktywne)';
      }
    }

    // No published posts yet
    if (task.status === 'active') {
      const typeLabel = task.content_type === 'reel' ? 'Reel' : task.content_type === 'story' ? 'Story' : 'zadanie';
      return `Zaplanowane ${typeLabel}`;
    } else {
      return 'Zadanie (nieaktywne)';
    }
  };

  const errorInfo = getErrorInfo(task);

  return (
    <div className="bg-white rounded-lg shadow p-4">
      {errorInfo && (
        <div className={`mb-4 p-4 rounded-lg border ${
          errorInfo.type === 'identity' || errorInfo.type === 'account_restricted'
            ? 'bg-amber-50 border-amber-300'
            : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-start gap-3">
            {errorInfo.type === 'identity' || errorInfo.type === 'account_restricted' ? (
              <ShieldAlert className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1 min-w-0">
              <h4 className={`text-sm font-semibold mb-1 ${
                errorInfo.type === 'identity' || errorInfo.type === 'account_restricted'
                  ? 'text-amber-900'
                  : 'text-red-800'
              }`}>
                {errorInfo.type === 'identity'
                  ? '⚠️ Wymagana weryfikacja tożsamości'
                  : errorInfo.type === 'account_restricted'
                    ? '🚫 Konto ograniczone'
                    : 'Błąd publikacji'}
              </h4>

              {/* IDENTITY VERIFICATION - Special panel with steps */}
              {errorInfo.type === 'identity' && (
                <div className="mt-2 space-y-3">
                  <p className="text-sm text-amber-800">
                    Facebook wymaga potwierdzenia Twojej tożsamości, aby publikować jako ta strona.
                  </p>
                  <div className="p-3 bg-amber-100/60 rounded-lg border border-amber-200 space-y-2">
                    <p className="text-xs font-semibold text-amber-900 uppercase tracking-wide">Jak rozwiązać:</p>
                    <div className="flex items-start gap-2">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px] font-bold mt-0.5">1</div>
                      <p className="text-sm text-amber-800">
                        <Smartphone className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                        Otwórz <span className="font-semibold">aplikację Facebook</span> na telefonie
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px] font-bold mt-0.5">2</div>
                      <p className="text-sm text-amber-800">
                        Przejdź do ustawień strony i <span className="font-semibold">potwierdź swoją tożsamość</span>
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px] font-bold mt-0.5">3</div>
                      <p className="text-sm text-amber-800">
                        <CheckCircle2 className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                        Po weryfikacji kliknij <span className="font-semibold">"Ponów publikację"</span> poniżej
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* ACCOUNT RESTRICTED - Special panel */}
              {errorInfo.type === 'account_restricted' && (
                <div className="mt-2 space-y-2">
                  <p className="text-sm text-amber-800">
                    {errorInfo.actionRequired}
                  </p>
                  <p className="text-xs text-amber-700">
                    Po rozwiązaniu problemu na Facebooku kliknij "Ponów publikację".
                  </p>
                </div>
              )}

              {/* OTHER ERRORS - Standard display */}
              {errorInfo.type !== 'identity' && errorInfo.type !== 'account_restricted' && (
                <>
                  <p className="text-sm text-red-700 break-words">
                    {errorInfo.message}
                  </p>

                  {errorInfo.isRetryActive && (
                    <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-md">
                      <div className="flex items-center gap-2 mb-2">
                        <RefreshCw className="w-4 h-4 text-orange-600 animate-spin" />
                        <span className="text-sm font-medium text-orange-800">
                          Automatyczne ponawianie aktywne
                        </span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-xs text-orange-700">
                          <span>Próba {errorInfo.attemptCount} z {errorInfo.maxAttempts}</span>
                          <span>Pozostało {errorInfo.attemptsRemaining}</span>
                        </div>
                        <div className="w-full bg-orange-200 rounded-full h-2">
                          <div
                            className="bg-orange-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${(errorInfo.attemptCount / errorInfo.maxAttempts) * 100}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-xs text-orange-600">
                          {errorInfo.nextRetry && (
                            <span>Następna próba: {format(errorInfo.nextRetry, 'HH:mm')}</span>
                          )}
                          <span>Pozostało {errorInfo.timeRemaining} min</span>
                        </div>
                      </div>
                      <p className="text-xs text-orange-600 mt-2">
                        System ponawia co {errorInfo.retryInterval} min przez max 1 godzinę.
                      </p>
                    </div>
                  )}

                  {!errorInfo.isRetryActive && errorInfo.canRetry && (
                    <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-md">
                      <p className="text-sm text-gray-700">
                        <span className="font-medium">Okno ponawiania wygasło.</span> Osiągnięto limit prób ({errorInfo.maxAttempts}).
                      </p>
                    </div>
                  )}

                  {!errorInfo.canRetry && (
                    <p className="text-sm text-red-600 mt-2">
                      <span className="font-medium">Wymagana akcja:</span> {errorInfo.actionRequired}
                    </p>
                  )}
                </>
              )}

              {errorInfo.timestamp && (
                <p className={`text-xs mt-2 ${errorInfo.type === 'identity' || errorInfo.type === 'account_restricted' ? 'text-amber-600' : 'text-red-500'}`}>
                  Pierwszy błąd: {format(new Date(errorInfo.timestamp), 'PPP p')}
                </p>
              )}

              <div className="flex flex-wrap gap-2 mt-3">
                {errorInfo.actionLink && (
                  <a
                    href={errorInfo.actionLink}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Połącz ponownie konto
                  </a>
                )}
                {onRetry && (
                  <button
                    onClick={onRetry}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      errorInfo.type === 'identity' || errorInfo.type === 'account_restricted'
                        ? 'text-amber-800 bg-amber-200 hover:bg-amber-300'
                        : 'text-red-700 bg-red-100 hover:bg-red-200'
                    }`}
                  >
                    <RefreshCw className="w-4 h-4" />
                    Ponów publikację
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            {renderPlatformIcon(task.platform)}
            {task.content_type === 'reel' && <Film className="w-5 h-5 text-purple-500" />}
            {task.content_type === 'story' && <Layers className="w-5 h-5 text-pink-500" />}
            <h3 className="text-lg font-semibold">{getTaskTitle()}</h3>
            {task.content_type && task.content_type !== 'post' && (
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${getContentTypeBadgeColors(task.content_type).bg} ${getContentTypeBadgeColors(task.content_type).text}`}>
                {getContentTypeBadgeLabel(task.content_type)}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {format(new Date(task.scheduled_for), 'PPP p')}
            </span>
            {task.cycle !== 'once' && (
              <span className="flex items-center gap-1">
                <Repeat className="w-4 h-4" />
                {task.cycle}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <button
            onClick={() => onAction('edit')}
            className="flex-1 sm:flex-none p-2 text-gray-400 hover:text-blue-500 rounded-full hover:bg-blue-50"
          >
            <Edit2 className="w-5 h-5" />
          </button>
          <button
            onClick={() => onAction('toggle')}
            className={`flex-1 sm:flex-none p-2 rounded-full ${
              task.status === 'active'
                ? 'text-green-400 hover:text-red-500 hover:bg-red-50'
                : 'text-red-400 hover:text-green-500 hover:bg-green-50'
            }`}
          >
            {task.status === 'active' ? (
              <PowerOff className="w-5 h-5" />
            ) : (
              <Power className="w-5 h-5" />
            )}
          </button>
          <button
            onClick={() => onAction('delete')}
            className="flex-1 sm:flex-none p-2 text-gray-400 hover:text-red-500 rounded-full hover:bg-red-50"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="mt-4 space-y-4">
        <div className="prose max-w-none">
          <FormattedText 
            content={task.content} 
            className="text-gray-700"
          />
        </div>

        {/* Reel/Story video preview with play overlay */}
        {task.video_url && (
          <ReelVideoPreview videoUrl={task.video_url} coverImageUrl={task.cover_image_url} />
        )}

        {/* Regular image preview (non-video) */}
        {task.image_url && !task.video_url && (
          <img
            src={task.image_url}
            alt="Task preview"
            className="rounded-lg w-full h-auto object-cover"
          />
        )}
      </div>
    </div>
  );
};

// Inline component for Reel video preview with Play button overlay
const ReelVideoPreview: React.FC<{ videoUrl: string; coverImageUrl?: string | null }> = ({ videoUrl, coverImageUrl }) => {
  const [isPlaying, setIsPlaying] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  const handlePlay = () => {
    setIsPlaying(true);
    setTimeout(() => {
      videoRef.current?.play();
    }, 100);
  };

  return (
    <div className="flex gap-3">
      {/* Video */}
      <div className="relative rounded-xl overflow-hidden bg-black shadow-md flex-1" style={{ maxWidth: coverImageUrl ? '50%' : '100%' }}>
        {!isPlaying ? (
          <div className="relative cursor-pointer group" onClick={handlePlay}>
            <video
              src={videoUrl}
              className="w-full aspect-[9/16] max-h-80 object-contain"
              muted
              preload="metadata"
            />
            {/* Play button overlay */}
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/40 transition-colors">
              <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <svg className="w-8 h-8 text-purple-600 ml-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </div>
            </div>
            {/* Badge */}
            <div className="absolute top-2 left-2 bg-purple-600/90 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
              <Film className="w-2.5 h-2.5" />
              Reel
            </div>
          </div>
        ) : (
          <video
            ref={videoRef}
            src={videoUrl}
            controls
            className="w-full aspect-[9/16] max-h-80 object-contain"
          />
        )}
      </div>

      {/* Cover image */}
      {coverImageUrl && (
        <div className="relative rounded-xl overflow-hidden bg-gray-100 shadow-md" style={{ maxWidth: '50%' }}>
          <img
            src={coverImageUrl}
            alt="Okładka Reel"
            className="w-full aspect-[9/16] max-h-80 object-cover"
          />
          <div className="absolute top-2 left-2 bg-gray-800/70 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
            <ImageIcon className="w-2.5 h-2.5" />
            Okładka
          </div>
        </div>
      )}
    </div>
  );
};